import { Button, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader, ModalOverlay, Text } from '@chakra-ui/react';
import { useKeystoneSession } from '../../auth/use-keystone-session';

import { lang } from '@lang';
import { useContext, type JSX } from 'react';
import { useNavigate } from 'react-router-dom';
import { AppContext, DesignActionTypes } from '@context';
import { deleteCustomerBlueprintRequest } from '@external';
import { getCanonicalDesignId } from '@helpers';
import { useAppToast } from '@hooks';
import { selectDeleteAllDraftsModal, selectNavigation, setContextPanelIsOpen, setDeleteAllDraftsModal, useAppDispatch, useAppSelector } from '@state';

export const DeleteAllDraftsModal = (): JSX.Element => {
	const appToast = useAppToast();

	const dispatch = useAppDispatch();
	const navigate = useNavigate();
	const state = useAppSelector(selectDeleteAllDraftsModal);
	const appNav = useAppSelector(selectNavigation);
	const { getToken } = useKeystoneSession();

	const { appDispatch, appState: { customerDesigns: designs }} = useContext(AppContext);

	const onClose = () => {
		dispatch(setDeleteAllDraftsModal(false));
	};

	const handleDelete = async (): Promise<void> => {
		const purges: Array<Promise<unknown>> = [];

		Object.keys(localStorage).forEach((key) => {
			if (key.startsWith('autosave-')) {
				localStorage.removeItem(key);
				const designId = key.replace('autosave-', '');
				appDispatch({ type: DesignActionTypes.setIsDirty, payload: { id: designId, isDirty: false }});

				// only designs already loaded for the current customer can be checked for a saved version — skip anything else rather than risk deleting a finalized design
				const loadedDesign = designs.designs.find((design) => design.blueprint.id === designId);
				const hasSavedVersion = loadedDesign?.blueprint.kitchens.children.some((k) => k.version !== null) ?? true;
				if (loadedDesign && !hasSavedVersion && designs.email) {
					purges.push(deleteCustomerBlueprintRequest({ email: designs.email, submissionId: getCanonicalDesignId(loadedDesign.blueprint), getToken }));
				}
			}
		});
		appToast.success({ title: lang.customer.message.allDraftsDeleted });
		dispatch(setDeleteAllDraftsModal(false));
		dispatch(setContextPanelIsOpen(false));

		// the reset-reload re-reads metadata.json, so the server purges must land first or the deleted drafts re-hydrate
		await Promise.all(purges);

		navigate(`/r/${appNav.email}/${appNav.designId}/?reset`);
	};

	return (
		<Modal isOpen={state.isOpen} onClose={onClose}>
			<ModalOverlay />
			<ModalContent>
				<ModalCloseButton />
				<>
					<ModalHeader>Warning</ModalHeader>
					<ModalBody>
						<Text>{lang.customer.message.deleteAllDraftsWarning}</Text>
					</ModalBody>

					<ModalFooter>
						<Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
						<Button colorScheme="green" onClick={handleDelete}>Continue</Button>
					</ModalFooter>
				</>
			</ModalContent>
		</Modal>
	);
};
